/* eslint-disable */

import React, { useRef, useState } from 'react'
import { FileSpreadsheet, Upload } from 'lucide-react'
import { Button } from '../../Components/ui/button'
import { Card, CardContent } from '../../Components/ui/card'
import { Label } from '../../Components/ui/label'
import { post } from '../../http/restservice'
import env from '../../env/env'
import { toast } from 'sonner'

const UPLOADS = [
  {
    key: 'master',
    title: 'BSE Scheme Master Upload',
    description: 'Upload the BSE scheme master Excel (.xlsx) to update and refresh scheme data in one go.',
    endpoint: `${env.API_URL}mutualfundservice/bse/scheme/master/upload`,
    accept: '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  },
  {
    key: 'sip',
    title: 'BSE Scheme SIP Upload',
    description: 'Upload the BSE scheme SIP Excel (.xlsx) to update SIP scheme master data.',
    endpoint: `${env.API_URL}mutualfundservice/bse/scheme/sip/upload`,
    accept: '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  },
]

const isXlsxFile = (file) => {
  if (!file) return false
  const name = file.name?.toLowerCase() || ''
  return (
    name.endsWith('.xlsx') ||
    file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  )
}

const BseUpload = () => {
  const [files, setFiles] = useState({ master: null, sip: null })
  const [loading, setLoading] = useState({ master: false, sip: false })
  const masterInputRef = useRef(null)
  const sipInputRef = useRef(null)
  const inputRefs = { master: masterInputRef, sip: sipInputRef }

  const handleFileChange = (key, event) => {
    const file = event.target.files?.[0]
    if (!file) {
      setFiles((prev) => ({ ...prev, [key]: null }))
      return
    }
    if (!isXlsxFile(file)) {
      toast.error('Please select an Excel file (.xlsx)')
      setFiles((prev) => ({ ...prev, [key]: null }))
      if (inputRefs[key].current) inputRefs[key].current.value = ''
      return
    }
    setFiles((prev) => ({ ...prev, [key]: file }))
  }

  const handleUpload = async (key, endpoint) => {
    const file = files[key]
    if (!file) {
      toast.error('Please select a file first')
      return
    }

    setLoading((prev) => ({ ...prev, [key]: true }))
    const formData = new FormData()
    formData.append('file', file, file.name)

    try {
      const response = await post(endpoint, formData, {
        'Content-Type': 'multipart/form-data',
      })

      if (response?.isError) {
        toast.error(
          response?.response?.message ||
          response?.message ||
          'Upload failed. Please try again.',
        )
        return
      }

      const successMessage =
        response?.message ||
        response?.response ||
        (typeof response === 'string' ? response : null) ||
        `${file.name} uploaded successfully`

      toast.success(typeof successMessage === 'string' ? successMessage : 'Upload successful')
      setFiles((prev) => ({ ...prev, [key]: null }))
      if (inputRefs[key].current) inputRefs[key].current.value = ''
    } catch (error) {
      toast.error(error?.message || 'Upload failed. Please try again.')
    } finally {
      setLoading((prev) => ({ ...prev, [key]: false }))
    }
  }

  return (
    <div className='mx-auto w-full max-w-none px-4 pb-8 pt-4 md:px-6 lg:px-8'>
      <div className='mb-5 md:mb-6'>
        <p className='mb-1 text-xs font-medium uppercase tracking-[0.08em] text-[var(--fb-text-muted)]'>
          Admin tools
        </p>
        <h1 className='font-IBM text-2xl font-bold tracking-tight text-[var(--fb-text-strong)] md:text-3xl'>
          BSE Upload
        </h1>
        <p className='mt-1 text-sm text-[var(--fb-text-muted)]'>
          Upload BSE scheme master and SIP Excel (.xlsx) files to update and refresh scheme data.
        </p>
      </div>

      <div className='grid w-full gap-4 md:gap-6 lg:grid-cols-2'>
        {UPLOADS.map(({ key, title, description, endpoint, accept }) => {
          const selected = files[key]
          const isLoading = loading[key]

          return (
            <Card
              key={key}
              className='overflow-hidden border border-[var(--fb-border)] bg-[var(--fb-surface)] shadow-[var(--fb-shadow-sm)] lg:rounded-3xl'
            >
              <CardContent className='p-5 md:p-8'>
                <div className='mb-5 flex items-start gap-3'>
                  <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[var(--fb-accent-soft)] text-[var(--fb-accent-text)]'>
                    <FileSpreadsheet className='h-6 w-6' aria-hidden='true' />
                  </div>
                  <div>
                    <p className='text-sm font-semibold text-[var(--fb-text-strong)] md:text-base'>
                      {title}
                    </p>
                    <p className='mt-1 text-sm text-[var(--fb-text-muted)]'>{description}</p>
                  </div>
                </div>

                <div className='space-y-3'>
                  <Label
                    htmlFor={`bse-upload-${key}`}
                    className='text-sm font-medium text-[var(--fb-text)]'
                  >
                    Excel file (.xlsx)
                  </Label>
                  <input
                    id={`bse-upload-${key}`}
                    ref={inputRefs[key]}
                    type='file'
                    accept={accept}
                    disabled={isLoading}
                    onChange={(e) => handleFileChange(key, e)}
                    className='block w-full cursor-pointer rounded-xl border border-[var(--fb-border)] bg-[var(--fb-surface-muted)] px-3 py-2.5 text-sm text-[var(--fb-text)] file:mr-3 file:rounded-lg file:border-0 file:bg-[var(--fb-accent-soft)] file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-[var(--fb-accent-text)]'
                  />
                  {selected && (
                    <p className='text-xs text-[var(--fb-text-muted)]'>
                      Selected: <span className='font-medium text-[var(--fb-text)]'>{selected.name}</span>
                    </p>
                  )}
                </div>

                <Button
                  onClick={() => handleUpload(key, endpoint)}
                  disabled={!selected || isLoading}
                  className='mt-5 h-11 w-full gap-2 rounded-xl bg-[var(--fb-accent)] text-sm font-semibold text-white hover:bg-[var(--fb-accent-hover)] disabled:bg-[var(--fb-accent)] disabled:opacity-50'
                >
                  <Upload className='h-4 w-4' aria-hidden='true' />
                  {isLoading ? 'Uploading...' : 'Upload'}
                </Button>
              </CardContent>
            </Card>
          )
        })}
      </div>
    </div>
  )
}

export default BseUpload
